import { useState, useEffect } from "react";
import { Star, ChevronDown } from "lucide-react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { API_URL } from "@/utils/apiConfig";

const API = `${API_URL || ""}/api`;

function StarDisplay({ rating, size = "w-3.5 h-3.5" }) {
  return (
    <div className="flex gap-px">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} className={`${size} ${i <= rating ? "text-amber-400 fill-amber-400" : "text-gray-200"}`} />
      ))}
    </div>
  );
}

export function ReviewsSection({ restaurantId, themeColors }) {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!restaurantId) return;
    axios.get(`${API}/public/reviews/${restaurantId}`)
      .then(res => setData(res.data))
      .catch(() => {});
  }, [restaurantId]);

  if (!data || !data.stats || data.stats.count === 0) return null;

  const { reviews, stats } = data;
  const tc = themeColors || { primary: "#16a34a", text: "#111827" };
  const displayed = showAll ? reviews : reviews.slice(0, 4);

  // Parse primary color for rgba
  let pr = 22, pg = 163, pb = 74;
  try {
    const hex = tc.primary?.replace("#", "") || "16a34a";
    pr = parseInt(hex.substring(0, 2), 16);
    pg = parseInt(hex.substring(2, 4), 16);
    pb = parseInt(hex.substring(4, 6), 16);
  } catch {}

  return (
    <div className="rounded-2xl p-4 sm:p-5 space-y-4" style={{ backgroundColor: `rgba(${pr},${pg},${pb},0.04)`, border: `1px solid rgba(${pr},${pg},${pb},0.1)` }} data-testid="reviews-section">
      {/* Header with average rating */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl" style={{ backgroundColor: `rgba(${pr},${pg},${pb},0.1)` }}>
            <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
            <span className="text-xl font-black" style={{ color: tc.text }}>{stats.avg}</span>
          </div>
          <div>
            <h3 className="text-sm sm:text-base font-bold" style={{ color: tc.text }}>{t('publicMenu.reviews')}</h3>
            <span className="text-xs" style={{ color: `${tc.text}70` }}>{stats.count} {stats.count === 1 ? t('publicMenu.reviewSingular') : t('publicMenu.reviewPlural')}</span>
          </div>
        </div>
        {/* Mini distribution */}
        <div className="hidden sm:flex items-center gap-1">
          {[5, 4, 3, 2, 1].map(r => {
            const count = stats[`r${r}`] || 0;
            if (count === 0) return null;
            const colors = { 5: "#22c55e", 4: "#84cc16", 3: "#eab308", 2: "#f97316", 1: "#ef4444" };
            return (
              <div key={r} className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: `${colors[r]}15`, color: colors[r] }}>
                <span className="font-bold">{r}</span>
                <Star className="w-2.5 h-2.5 fill-current" />
                <span className="font-medium">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Distribution bar */}
      <div className="flex gap-0.5 h-2 rounded-full overflow-hidden" data-testid="rating-distribution">
        {[5, 4, 3, 2, 1].map(r => {
          const pct = stats.count > 0 ? ((stats[`r${r}`] || 0) / stats.count) * 100 : 0;
          if (pct === 0) return null;
          const colors = { 5: "#22c55e", 4: "#84cc16", 3: "#eab308", 2: "#f97316", 1: "#ef4444" };
          return <div key={r} style={{ width: `${pct}%`, backgroundColor: colors[r] }} className="rounded-full" />;
        })}
      </div>

      {/* Reviews grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {displayed.map(review => {
          const dateLocale = i18n.language === 'pt' ? 'pt-PT' : i18n.language === 'en' ? 'en-US' : 'es-ES';
          const date = review.created_at ? new Date(review.created_at).toLocaleDateString(dateLocale, { day: "numeric", month: "short" }) : "";
          return (
            <div key={review.id} className="rounded-xl p-3 border" style={{ backgroundColor: `rgba(255,255,255,0.7)`, borderColor: `rgba(${pr},${pg},${pb},0.08)` }} data-testid={`public-review-${review.id}`}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold text-white"
                    style={{ backgroundColor: tc.primary }}>
                    {(review.customer_name || "C").charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <span className="text-xs font-semibold text-gray-800 block leading-tight">{review.customer_name || "Cliente"}</span>
                    <span className="text-[10px] text-gray-400">{date}</span>
                  </div>
                </div>
                <StarDisplay rating={review.rating} size="w-3 h-3" />
              </div>
              {review.comment && (
                <p className="text-xs text-gray-600 leading-relaxed">{review.comment}</p>
              )}
              {review.admin_reply && (
                <div className="mt-2 pl-2.5 border-l-2 rounded-r-md p-1.5" style={{ borderColor: tc.primary, backgroundColor: `rgba(${pr},${pg},${pb},0.04)` }}>
                  <p className="text-[10px] font-semibold" style={{ color: tc.primary }}>{t('publicMenu.restaurantReply')}</p>
                  <p className="text-[11px] text-gray-600">{review.admin_reply}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {reviews.length > 4 && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="flex items-center gap-1 mx-auto text-xs font-semibold py-2 px-5 rounded-full transition hover:opacity-80"
          style={{ color: tc.primary, backgroundColor: `rgba(${pr},${pg},${pb},0.08)` }}
          data-testid="show-more-reviews"
        >
          {showAll ? t('publicMenu.seeLess') : `${t('publicMenu.seeAll')} (${reviews.length})`}
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showAll ? "rotate-180" : ""}`} />
        </button>
      )}
    </div>
  );
}
